import { useEffect, useState } from 'react';
import { authService } from '../services/authService';
import { diveSiteListService } from '../services/diveSiteListService';
import type { DiveSiteList, DiveSiteListVisibility } from '../types/DiveSite';
import { preferencesManager } from '../utils/preferencesManager';

export interface DiveSitePreferencesState {
  favoriteSiteIds: string[];
  wishlistSiteIds: string[];
  customLists: DiveSiteList[];
  ignoredDiveSiteMatches: Record<string, string[]>;
}

const CUSTOM_SETTINGS_KEY = 'diveSitePreferences';
const GUEST_PREFERENCES_STORAGE_KEY = 'aidivecoach_dive_site_preferences_guest';
const GUEST_LIST_STORAGE_KEY = 'aidivecoach_dive_site_lists_guest';

const emptyPreferences = (): DiveSitePreferencesState => ({
  favoriteSiteIds: [],
  wishlistSiteIds: [],
  customLists: [],
  ignoredDiveSiteMatches: {},
});

const sortDiveSiteLists = (lists: DiveSiteList[]): DiveSiteList[] => {
  return [...lists].sort((left, right) => {
    const updatedComparison = new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime();
    if (updatedComparison !== 0) {
      return updatedComparison;
    }

    return left.name.localeCompare(right.name);
  });
};

const sanitizeCustomLists = (value: unknown): DiveSiteList[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return sortDiveSiteLists(value.reduce<DiveSiteList[]>((result, item) => {
    if (!item || typeof item !== 'object') {
      return result;
    }

    const record = item as Record<string, unknown>;
    const listId = typeof record.listId === 'string' ? record.listId : null;
    const name = typeof record.name === 'string' ? record.name.trim() : null;

    if (!listId || !name) {
      return result;
    }

    const description = typeof record.description === 'string' && record.description.trim().length > 0
      ? record.description.trim()
      : undefined;
    const siteIds = Array.isArray(record.siteIds)
      ? Array.from(new Set(record.siteIds.filter((siteId): siteId is string => typeof siteId === 'string')))
      : [];
    const visibility = record.visibility === 'link' || record.visibility === 'invite-only' ? record.visibility : 'private';
    const ownerUserId = typeof record.ownerUserId === 'string' ? record.ownerUserId : 'guest';
    const ownerDisplayName = typeof record.ownerDisplayName === 'string' && record.ownerDisplayName.trim().length > 0
      ? record.ownerDisplayName.trim()
      : 'New Achievement Scuba diver';
    const shareCode = typeof record.shareCode === 'string' && record.shareCode.trim().length > 0
      ? record.shareCode.trim()
      : undefined;
    const collaboratorUserIds = Array.isArray(record.collaboratorUserIds)
      ? Array.from(new Set(record.collaboratorUserIds.filter((userId): userId is string => typeof userId === 'string')))
      : [];
    const createdAt = typeof record.createdAt === 'string' ? record.createdAt : new Date().toISOString();
    const updatedAt = typeof record.updatedAt === 'string' ? record.updatedAt : createdAt;
    const siteSchedules: Record<string, string> =
      record.siteSchedules && typeof record.siteSchedules === 'object' && !Array.isArray(record.siteSchedules)
        ? Object.fromEntries(
            Object.entries(record.siteSchedules).filter(([, v]) => typeof v === 'string')
          )
        : {};
    const divedByUserIds: Record<string, string[]> =
      record.divedByUserIds && typeof record.divedByUserIds === 'object' && !Array.isArray(record.divedByUserIds)
        ? Object.fromEntries(
            Object.entries(record.divedByUserIds)
              .filter(([, v]) => Array.isArray(v))
              .map(([k, v]) => [k, (v as unknown[]).filter((uid): uid is string => typeof uid === 'string')])
          )
        : {};

    result.push({
      listId,
      ownerUserId,
      ownerDisplayName,
      name,
      description,
      siteIds,
      visibility,
      shareCode,
      collaboratorUserIds,
      siteSchedules,
      divedByUserIds,
      createdAt,
      updatedAt,
    });

    return result;
  }, []));
};

const sanitizePreferences = (value: any): DiveSitePreferencesState => ({
  favoriteSiteIds: Array.isArray(value?.favoriteSiteIds) ? value.favoriteSiteIds.filter((siteId: unknown) => typeof siteId === 'string') : [],
  wishlistSiteIds: Array.isArray(value?.wishlistSiteIds) ? value.wishlistSiteIds.filter((siteId: unknown) => typeof siteId === 'string') : [],
  customLists: sanitizeCustomLists(value?.customLists),
  ignoredDiveSiteMatches: value?.ignoredDiveSiteMatches && typeof value.ignoredDiveSiteMatches === 'object'
    ? Object.entries(value.ignoredDiveSiteMatches).reduce<Record<string, string[]>>((result, [diveId, siteIds]) => {
      if (typeof diveId !== 'string' || !Array.isArray(siteIds)) {
        return result;
      }

      const sanitizedSiteIds = siteIds.filter((siteId: unknown): siteId is string => typeof siteId === 'string');
      if (sanitizedSiteIds.length > 0) {
        result[diveId] = Array.from(new Set(sanitizedSiteIds));
      }

      return result;
    }, {})
    : {},
});

const readLocalPreferences = (storageKey: string): DiveSitePreferencesState => {
  try {
    const rawValue = localStorage.getItem(storageKey);
    return rawValue ? sanitizePreferences(JSON.parse(rawValue)) : emptyPreferences();
  } catch (error) {
    console.warn('Failed to read dive site preferences from local storage:', error);
    return emptyPreferences();
  }
};

const writeLocalPreferences = (storageKey: string, value: DiveSitePreferencesState): void => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(value));
  } catch (error) {
    console.warn('Failed to cache dive site preferences locally:', error);
  }
};

const readLocalDiveSiteLists = (storageKey: string): DiveSiteList[] => {
  try {
    const rawValue = localStorage.getItem(storageKey);
    return rawValue ? sanitizeCustomLists(JSON.parse(rawValue)) : [];
  } catch (error) {
    console.warn('Failed to read dive site lists from local storage:', error);
    return [];
  }
};

const writeLocalDiveSiteLists = (storageKey: string, value: DiveSiteList[]): void => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(value));
  } catch (error) {
    console.warn('Failed to cache dive site lists locally:', error);
  }
};

const getUserId = (): string | null => {
  const currentUserId = preferencesManager.getUserId();
  if (currentUserId) {
    return currentUserId;
  }

  const user = authService.getUser();
  if (!user?.userId) {
    return null;
  }

  preferencesManager.setUserId(user.userId);
  return user.userId;
};

const getStorageKey = (userId: string | null): string => {
  return userId ? `aidivecoach_dive_site_preferences_${userId}` : GUEST_PREFERENCES_STORAGE_KEY;
};

const getListStorageKey = (userId: string | null): string => {
  return userId ? `aidivecoach_dive_site_lists_${userId}` : GUEST_LIST_STORAGE_KEY;
};

const toggleSiteId = (items: string[], siteId: string): string[] => {
  return items.includes(siteId)
    ? items.filter((currentId) => currentId !== siteId)
    : [...items, siteId];
};

const buildCustomListId = (): string => {
  return `dsl-local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

const withoutCustomLists = (preferences: DiveSitePreferencesState): DiveSitePreferencesState => ({
  ...preferences,
  customLists: [],
});

const buildGuestList = (name: string, description?: string): DiveSiteList => {
  const timestamp = new Date().toISOString();

  return {
    listId: buildCustomListId(),
    ownerUserId: 'guest',
    ownerDisplayName: 'Guest diver',
    name,
    description,
    siteIds: [],
    visibility: 'private',
    shareCode: undefined,
    collaboratorUserIds: [],
    siteSchedules: {},
    divedByUserIds: {},
    createdAt: timestamp,
    updatedAt: timestamp,
  };
};

type UpdateCustomListValues = {
  name?: string;
  description?: string;
  visibility?: DiveSiteListVisibility;
  collaboratorUserIds?: string[];
};

export const useDiveSitePreferences = () => {
  const [preferences, setPreferences] = useState<DiveSitePreferencesState>(emptyPreferences());
  const [customLists, setCustomLists] = useState<DiveSiteList[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyCustomLists = (nextLists: DiveSiteList[]) => {
    const normalizedLists = sortDiveSiteLists(nextLists);
    setCustomLists(normalizedLists);
    setPreferences((currentPreferences) => ({
      ...currentPreferences,
      customLists: normalizedLists,
    }));
    return normalizedLists;
  };

  useEffect(() => {
    const loadPreferences = async () => {
      const userId = getUserId();
      const storageKey = getStorageKey(userId);
      const listStorageKey = getListStorageKey(userId);

      try {
        setLoading(true);
        setError(null);

        if (!userId) {
          const cachedPreferences = readLocalPreferences(storageKey);
          const cachedLists = readLocalDiveSiteLists(listStorageKey);
          const nextLists = cachedLists.length > 0 ? cachedLists : cachedPreferences.customLists;
          const normalizedLists = sortDiveSiteLists(nextLists);

          if (cachedLists.length === 0 && cachedPreferences.customLists.length > 0) {
            writeLocalDiveSiteLists(listStorageKey, normalizedLists);
          }

          setCustomLists(normalizedLists);
          setPreferences({
            ...cachedPreferences,
            customLists: normalizedLists,
          });
          return;
        }

        const currentPreferences = preferencesManager.getPreferences() ?? await preferencesManager.loadPreferences();
        const sitePreferences = sanitizePreferences(currentPreferences.customSettings?.[CUSTOM_SETTINGS_KEY]);
        const syncedLists = await diveSiteListService.getMyDiveSiteLists();

        let nextLists = syncedLists;
        if (syncedLists.length === 0 && sitePreferences.customLists.length > 0) {
          const migratedLists: DiveSiteList[] = [];

          for (const legacyList of sitePreferences.customLists) {
            const createdList = await diveSiteListService.createDiveSiteList({
              name: legacyList.name,
              description: legacyList.description,
              siteIds: legacyList.siteIds,
              visibility: legacyList.visibility,
              collaboratorUserIds: legacyList.collaboratorUserIds,
            });
            migratedLists.push(createdList);
          }

          nextLists = migratedLists;
        }

        const normalizedLists = sortDiveSiteLists(nextLists);
        setCustomLists(normalizedLists);
        setPreferences({
          ...withoutCustomLists(sitePreferences),
          customLists: normalizedLists,
        });
        writeLocalPreferences(storageKey, withoutCustomLists(sitePreferences));
        writeLocalDiveSiteLists(listStorageKey, normalizedLists);
      } catch (loadError) {
        console.error('Failed to load dive site preferences:', loadError);
        const cachedPreferences = readLocalPreferences(storageKey);
        const cachedLists = readLocalDiveSiteLists(listStorageKey);
        const fallbackLists = cachedLists.length > 0 ? cachedLists : cachedPreferences.customLists;
        const normalizedLists = sortDiveSiteLists(fallbackLists);

        setError('Unable to sync dive site lists right now. Using cached lists on this device.');
        setCustomLists(normalizedLists);
        setPreferences({
          ...cachedPreferences,
          customLists: normalizedLists,
        });
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, []);

  const persistPreferences = async (nextPreferences: DiveSitePreferencesState) => {
    const userId = getUserId();
    const storageKey = getStorageKey(userId);

    setPreferences({
      ...nextPreferences,
      customLists,
    });
    writeLocalPreferences(storageKey, withoutCustomLists(nextPreferences));

    if (!userId) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const currentPreferences = preferencesManager.getPreferences() ?? await preferencesManager.loadPreferences();
      await preferencesManager.savePreferences({
        customSettings: {
          ...(currentPreferences.customSettings ?? {}),
          [CUSTOM_SETTINGS_KEY]: withoutCustomLists(nextPreferences),
        },
      });
    } catch (saveError) {
      console.error('Failed to save dive site preferences:', saveError);
      setError('Unable to sync dive site lists right now. Your changes are cached locally on this device.');
    } finally {
      setSaving(false);
    }
  };

  const toggleFavorite = async (siteId: string) => {
    await persistPreferences({
      ...preferences,
      favoriteSiteIds: toggleSiteId(preferences.favoriteSiteIds, siteId),
    });
  };

  const toggleWishlist = async (siteId: string) => {
    await persistPreferences({
      ...preferences,
      wishlistSiteIds: toggleSiteId(preferences.wishlistSiteIds, siteId),
    });
  };

  const createCustomList = async (name: string, description?: string, visibility: DiveSiteListVisibility = 'private') => {
    const trimmedName = name.trim();
    const trimmedDescription = description?.trim();
    const userId = getUserId();
    const listStorageKey = getListStorageKey(userId);

    if (!trimmedName) {
      throw new Error('List name is required');
    }

    if (customLists.some((list) => list.name.toLowerCase() === trimmedName.toLowerCase())) {
      throw new Error('A list with that name already exists');
    }

    if (!userId) {
      setSaving(true);
      try {
        setError(null);
        const nextList = buildGuestList(trimmedName, trimmedDescription || undefined);
        const nextLists = applyCustomLists([...customLists, nextList]);
        writeLocalDiveSiteLists(listStorageKey, nextLists);
        return nextList;
      } finally {
        setSaving(false);
      }
    }

    try {
      setSaving(true);
      setError(null);
      const nextList = await diveSiteListService.createDiveSiteList({
        name: trimmedName,
        description: trimmedDescription || undefined,
        siteIds: [],
        visibility,
        collaboratorUserIds: [],
      });
      const nextLists = applyCustomLists([...customLists, nextList]);
      writeLocalDiveSiteLists(listStorageKey, nextLists);
      return nextList;
    } catch (saveError) {
      console.error('Failed to create dive site list:', saveError);
      setError('Unable to create dive site list right now.');
      throw saveError;
    } finally {
      setSaving(false);
    }
  };

  const deleteCustomList = async (listId: string) => {
    const userId = getUserId();
    const listStorageKey = getListStorageKey(userId);

    if (!userId) {
      setSaving(true);
      try {
        setError(null);
        const nextLists = applyCustomLists(customLists.filter((list) => list.listId !== listId));
        writeLocalDiveSiteLists(listStorageKey, nextLists);
      } finally {
        setSaving(false);
      }
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await diveSiteListService.deleteDiveSiteList(listId);
      const nextLists = applyCustomLists(customLists.filter((list) => list.listId !== listId));
      writeLocalDiveSiteLists(listStorageKey, nextLists);
    } catch (saveError) {
      console.error('Failed to delete dive site list:', saveError);
      setError('Unable to delete dive site list right now.');
      throw saveError;
    } finally {
      setSaving(false);
    }
  };

  const toggleSiteInCustomList = async (listId: string, siteId: string) => {
    const currentList = customLists.find((list) => list.listId === listId);
    const userId = getUserId();

    if (!currentList) {
      throw new Error('Dive-site list not found');
    }

    const nextSiteIds = toggleSiteId(currentList.siteIds, siteId);
    const listStorageKey = getListStorageKey(userId);

    if (!userId) {
      setSaving(true);
      try {
        setError(null);
        const updatedAt = new Date().toISOString();
        const nextLists = applyCustomLists(customLists.map((list) => {
          if (list.listId !== listId) {
            return list;
          }

          return {
            ...list,
            siteIds: nextSiteIds,
            updatedAt,
          };
        }));
        writeLocalDiveSiteLists(listStorageKey, nextLists);
      } finally {
        setSaving(false);
      }
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const updatedList = await diveSiteListService.updateDiveSiteList({
        listId,
        name: currentList.name,
        description: currentList.description,
        siteIds: nextSiteIds,
        visibility: currentList.visibility,
        collaboratorUserIds: currentList.collaboratorUserIds,
      });
      const nextLists = applyCustomLists(customLists.map((list) => (list.listId === listId ? updatedList : list)));
      writeLocalDiveSiteLists(listStorageKey, nextLists);
    } catch (saveError) {
      console.error('Failed to update dive site list:', saveError);
      setError('Unable to update dive site list right now.');
      throw saveError;
    } finally {
      setSaving(false);
    }
  };

  const updateCustomList = async (listId: string, updates: UpdateCustomListValues) => {
    const currentList = customLists.find((list) => list.listId === listId);
    const userId = getUserId();

    if (!currentList) {
      throw new Error('Dive-site list not found');
    }

    const nextName = updates.name?.trim() ?? currentList.name;
    const nextDescription = updates.description?.trim() || undefined;
    const nextVisibility = updates.visibility ?? currentList.visibility;
    const nextCollaboratorUserIds = nextVisibility === 'private'
      ? []
      : updates.collaboratorUserIds
      ? Array.from(new Set(updates.collaboratorUserIds.map((value) => value.trim()).filter(Boolean)))
      : currentList.collaboratorUserIds;
    const listStorageKey = getListStorageKey(userId);

    if (!nextName) {
      throw new Error('List name is required');
    }

    if (customLists.some((list) => list.listId !== listId && list.name.toLowerCase() === nextName.toLowerCase())) {
      throw new Error('A list with that name already exists');
    }

    if (!userId) {
      setSaving(true);
      try {
        setError(null);
        const updatedAt = new Date().toISOString();
        const nextLists = applyCustomLists(customLists.map((list) => {
          if (list.listId !== listId) {
            return list;
          }

          return {
            ...list,
            name: nextName,
            description: nextDescription,
            visibility: 'private',
            collaboratorUserIds: [],
            shareCode: undefined,
            updatedAt,
          };
        }));
        writeLocalDiveSiteLists(listStorageKey, nextLists);
        return nextLists.find((list) => list.listId === listId) ?? currentList;
      } finally {
        setSaving(false);
      }
    }

    try {
      setSaving(true);
      setError(null);
      const updatedList = await diveSiteListService.updateDiveSiteList({
        listId,
        name: nextName,
        description: nextDescription,
        siteIds: currentList.siteIds,
        visibility: nextVisibility,
        collaboratorUserIds: nextCollaboratorUserIds,
      });
      const nextLists = applyCustomLists(customLists.map((list) => (list.listId === listId ? updatedList : list)));
      writeLocalDiveSiteLists(listStorageKey, nextLists);
      return updatedList;
    } catch (saveError) {
      console.error('Failed to update dive site list metadata:', saveError);
      setError('Unable to update dive site list right now.');
      throw saveError;
    } finally {
      setSaving(false);
    }
  };

  const getCustomListsForSite = (siteId: string) => {
    return customLists.filter((list) => list.siteIds.includes(siteId));
  };

  const setDiveSiteMatchIgnored = async (diveId: string, siteId: string, ignored: boolean) => {
    const currentIgnoredMatches = preferences.ignoredDiveSiteMatches[diveId] ?? [];
    const nextIgnoredMatches = ignored
      ? Array.from(new Set([...currentIgnoredMatches, siteId]))
      : currentIgnoredMatches.filter((currentSiteId) => currentSiteId !== siteId);

    const ignoredDiveSiteMatches = { ...preferences.ignoredDiveSiteMatches };
    if (nextIgnoredMatches.length > 0) {
      ignoredDiveSiteMatches[diveId] = nextIgnoredMatches;
    } else {
      delete ignoredDiveSiteMatches[diveId];
    }

    await persistPreferences({
      ...preferences,
      ignoredDiveSiteMatches,
    });
  };

  return {
    preferences,
    loading,
    saving,
    error,
    customLists,
    isFavorite: (siteId: string) => preferences.favoriteSiteIds.includes(siteId),
    isWishlisted: (siteId: string) => preferences.wishlistSiteIds.includes(siteId),
    isSiteInCustomList: (listId: string, siteId: string) => customLists.some((list) => list.listId === listId && list.siteIds.includes(siteId)),
    getCustomListsForSite,
    isDiveSiteMatchIgnored: (diveId: string, siteId: string) => (preferences.ignoredDiveSiteMatches[diveId] ?? []).includes(siteId),
    toggleFavorite,
    toggleWishlist,
    createCustomList,
    deleteCustomList,
    toggleSiteInCustomList,
    updateCustomList,
    setDiveSiteMatchIgnored,
  };
};